import { useState, useEffect, useCallback } from 'react';
import '../../Styles/ArtistRoot.css';
import '../../Styles/Admin.css';

import { Link, useNavigate } from "react-router-dom";
import { MdAdd, MdClose, MdPerson, MdHeadphones, MdFavorite } from 'react-icons/md';
import { BiSolidPlaylist } from 'react-icons/bi';
import { Autocomplete, TextField } from '@mui/material';
import izenuAPI from '../../services/izenuAPI';

const COVER_MAX_MB = 10;

export default function Playlists() {
    const navigate = useNavigate();
    const [playlists, setPlaylists] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [verifyFilter, setVerifyFilter] = useState('all'); // 'all' | 'verified' | 'unverified'
    const [sortBy, setSortBy] = useState('streams'); // 'streams' | 'followers' | 'recent'

    // Create form state
    const [artists, setArtists] = useState([]);
    const [selectedArtist, setSelectedArtist] = useState(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [coverFile, setCoverFile] = useState(null);
    const [coverPreview, setCoverPreview] = useState(null);
    const [coverError, setCoverError] = useState('');
    const [creating, setCreating] = useState(false);

    const distributorId = localStorage.getItem('id');

    const listPlaylists = useCallback(async () => {
        try {
            const { data: r } = await izenuAPI.get('/distributor/playlists/listPlaylists.php');
            if (r.response) {
                setPlaylists(r.playlists || []);
            }
        } catch (err) {
            console.log(err);
        }
        setLoaded(true);
    }, []);

    const fetchArtists = useCallback(async () => {
        try {
            const { data: r } = await izenuAPI.get('/distributor/artist/fetchArtists.php?distributor_id=' + distributorId);
            if (r.artists) {
                setArtists(r.artists);
            }
        } catch (err) {
            console.log(err);
        }
    }, [distributorId]);

    const handleCreate = useCallback(async () => {
        if (!selectedArtist || !title.trim()) {
            alert('Escolhe um artista e preenche o título.');
            return;
        }
        setCreating(true);
        try {
            const { data: r } = await izenuAPI.post('/distributor/playlists/createPlaylist.php', {
                title: title.trim(),
                info: description.trim(),
                country: 'Angola',
                user_id: selectedArtist.user_id,
                cover: ''
            });
            if (!r.response) {
                alert(r.message || 'Não foi possível criar a playlist.');
                setCreating(false);
                return;
            }
            // The cover chosen here used to be dropped: the create call sends no
            // file. It goes up now, once the playlist has an id.
            if (coverFile && r.playlist_id) {
                try {
                    const formData = new FormData();
                    formData.append('cover', coverFile);
                    formData.append('playlist_id', r.playlist_id);
                    await izenuAPI.post('/distributor/playlists/uploadPlaylistCover.php', formData);
                } catch (coverErr) {
                    console.log('Cover upload failed:', coverErr);
                    alert('A playlist foi criada, mas não foi possível enviar a capa. Podes enviá-la na página da playlist.');
                }
            }
            setShowCreate(false);
            setTitle('');
            setDescription('');
            setSelectedArtist(null);
            setCoverFile(null);
            setCoverPreview(null);
            if (r.playlist_id) {
                navigate('/playlist/' + r.playlist_id);
            } else {
                listPlaylists();
            }
        } catch (err) {
            console.log(err);
            alert(err?.response?.data?.message || 'Erro ao criar playlist');
        }
        setCreating(false);
    }, [selectedArtist, title, description, coverFile, listPlaylists, navigate]);

    const handleCoverChange = useCallback((e) => {
        const file = e.target.files[0];
        e.target.value = '';
        setCoverError('');
        if (!file) return;
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        if (!['jpg', 'jpeg', 'png', 'webp'].includes(ext)) {
            setCoverError('Formato não suportado. Usa JPG, PNG ou WEBP.');
            return;
        }
        if (file.size > COVER_MAX_MB * 1024 * 1024) {
            setCoverError(`A imagem tem ${(file.size / 1024 / 1024).toFixed(1)} MB. O máximo é ${COVER_MAX_MB} MB.`);
            return;
        }
        setCoverFile(file);
        setCoverPreview(URL.createObjectURL(file));
    }, []);

    useEffect(() => () => { if (coverPreview) URL.revokeObjectURL(coverPreview); }, [coverPreview]);

    useEffect(() => {
        listPlaylists();
    }, [listPlaylists]);

    const coverUrl = (cover) => {
        if (!cover) return null;
        const ext = (cover.split('/').pop().split('.').pop() || '').toLowerCase();
        return ['jpg', 'jpeg', 'png', 'webp'].includes(ext) ? cover : null;
    };

    const counts = {
        all: playlists.length,
        verified: playlists.filter(e => e.verified).length,
        unverified: playlists.filter(e => !e.verified).length,
        mine: playlists.filter(e => e.created_by_me).length,
    };

    const filteredPlaylists = playlists.filter((e) => {
        if (verifyFilter === 'verified' && !e.verified) return false;
        if (verifyFilter === 'unverified' && e.verified) return false;
        if (verifyFilter === 'mine' && !e.created_by_me) return false;
        if (!searchTerm.trim()) return true;
        const term = searchTerm.toLowerCase();
        return (e.title || '').toLowerCase().includes(term)
            || (e.artist_name || '').toLowerCase().includes(term)
            || (e.name || '').toLowerCase().includes(term);
    });

    // Most played first, ties broken by followers, then newest.
    const SORTS = {
        streams: (a, b) => (b.streams_count || 0) - (a.streams_count || 0) || (b.followers_count || 0) - (a.followers_count || 0) || b.playlist_id - a.playlist_id,
        followers: (a, b) => (b.followers_count || 0) - (a.followers_count || 0) || (b.streams_count || 0) - (a.streams_count || 0) || b.playlist_id - a.playlist_id,
        recent: (a, b) => b.playlist_id - a.playlist_id,
    };
    filteredPlaylists.sort(SORTS[sortBy]);

    const inputCls = 'block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-amber-500 focus:ring-amber-500';

    return (
        <div className="mx-auto max-w-7xl p-3 sm:p-6 text-left">
            {/* Header */}
            <div className="mb-4 flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Playlists</h1>
                    <p className="text-sm text-gray-500">{counts.all} {counts.all === 1 ? 'playlist' : 'playlists'} · {counts.verified} {counts.verified === 1 ? 'verificada' : 'verificadas'}</p>
                </div>
                <button type="button" onClick={() => { setShowCreate(v => !v); if (artists.length === 0) fetchArtists(); }}
                    className="flex items-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-medium !text-white hover:bg-amber-600">
                    {showCreate ? <MdClose size={18} /> : <MdAdd size={18} />} {showCreate ? 'Fechar' : 'Criar playlist'}
                </button>
            </div>
            <p className="mb-4 max-w-3xl text-sm text-gray-500">
                Só as playlists <strong className="font-medium text-gray-700">verificadas pela Izenu</strong> e públicas aparecem na pesquisa e na descoberta da app e do site. As restantes só são vistas por quem tiver o link, e as privadas só pelo dono.
            </p>

            {/* Create playlist form */}
            {showCreate && (
                <section className="mb-6 rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
                    <h2 className="mb-4 text-lg font-semibold text-gray-900">Nova playlist</h2>
                    <div className="flex flex-col gap-5 sm:flex-row">
                        <div className="flex shrink-0 flex-col items-center sm:w-48">
                            <label className="relative flex h-40 w-40 cursor-pointer items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 hover:border-amber-400">
                                {coverPreview
                                    ? <img src={coverPreview} className="h-full w-full object-cover" alt="" />
                                    : <span className="flex flex-col items-center gap-1 text-sm text-gray-500"><BiSolidPlaylist size={40} className="text-gray-400" />Escolher capa</span>}
                                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleCoverChange} className="sr-only" />
                            </label>
                            <ul className="mt-2 w-full space-y-0.5 text-xs [&_li]:!p-0 [&_li]:!text-gray-500">
                                <li><strong className="font-medium text-gray-700">Formato:</strong> JPG, PNG ou WEBP</li>
                                <li><strong className="font-medium text-gray-700">Tamanho ideal:</strong> quadrada, 1200 × 1200 px ou mais</li>
                                <li><strong className="font-medium text-gray-700">Peso máximo:</strong> {COVER_MAX_MB} MB</li>
                            </ul>
                            {coverError && <p className="mt-1 w-full text-xs text-red-600">{coverError}</p>}
                        </div>

                        <div className="grid min-w-0 flex-1 grid-cols-1 content-start gap-3">
                            <div>
                                <Autocomplete
                                    options={artists}
                                    getOptionLabel={(o) => o.artist_name || o.name || ''}
                                    value={selectedArtist}
                                    onChange={(e, v) => setSelectedArtist(v)}
                                    renderInput={(params) => <TextField {...params} label="Dono da playlist (artista)" size="small" />}
                                    size="small"
                                />
                                <p className="mt-1 text-xs text-gray-500">A playlist fica no perfil deste artista, mas pode ter músicas de qualquer artista da distribuidora.</p>
                            </div>
                            <label className="block">
                                <span className="mb-1 block text-sm font-medium text-gray-700">Título *</span>
                                <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} />
                            </label>
                            <label className="block">
                                <span className="mb-1 block text-sm font-medium text-gray-700">Descrição</span>
                                <textarea className={`${inputCls} resize-y`} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Opcional" />
                            </label>
                            <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
                                <button type="button" onClick={() => setShowCreate(false)}
                                    className="rounded-lg !border !border-solid border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancelar</button>
                                <button type="button" onClick={handleCreate} disabled={creating || !selectedArtist || !title.trim()}
                                    className="rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-medium !text-white hover:bg-amber-600 disabled:opacity-50">
                                    {creating ? 'A criar…' : 'Criar playlist'}
                                </button>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Filters */}
            <div className="mb-5 flex flex-wrap items-center gap-2">
                {[
                    { key: 'all', label: 'Todas' },
                    { key: 'verified', label: 'Verificadas' },
                    { key: 'unverified', label: 'Por verificar' },
                    { key: 'mine', label: 'Criadas por ti' },
                ].map(opt => (
                    <button key={opt.key} type="button" onClick={() => setVerifyFilter(opt.key)}
                        className={`rounded-full px-4 py-1.5 text-sm font-medium transition !border !border-solid ${verifyFilter === opt.key ? 'border-amber-500 bg-amber-500 !text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}>
                        {opt.label} <span className="opacity-70">{counts[opt.key]}</span>
                    </button>
                ))}
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} aria-label="Ordenar"
                    className="ms-auto w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-amber-500 focus:ring-amber-500 sm:w-auto">
                    <option value="streams">Mais ouvidas</option>
                    <option value="followers">Mais seguidas</option>
                    <option value="recent">Mais recentes</option>
                </select>
                <div className="search-distributors-input w-full sm:w-auto sm:min-w-[280px]">
                    <input type="search" placeholder="Pesquisar por título ou artista…" value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)} style={{ padding: '8px 0', fontSize: 14 }} />
                </div>
            </div>

            {/* Grid */}
            {!loaded ? (
                <p className="py-16 text-center text-sm text-gray-500">A carregar…</p>
            ) : playlists.length === 0 ? (
                <p className="py-16 text-center text-sm text-gray-500">Ainda não há playlists. Cria a primeira com "Criar playlist".</p>
            ) : filteredPlaylists.length === 0 ? (
                <p className="py-16 text-center text-sm text-gray-500">
                    {verifyFilter === 'mine' && !searchTerm
                        ? 'Ainda não há playlists criadas por ti aqui. Só contam as criadas no painel a partir de agora; as mais antigas aparecem em "Todas".'
                        : `Nenhuma playlist encontrada${searchTerm ? ` para "${searchTerm}"` : ''}.`}
                </p>
            ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 xl:grid-cols-5">
                    {filteredPlaylists.map((e) => {
                        const cover = coverUrl(e.cover);
                        return (
                            <Link key={e.playlist_id} to={`/playlist/${e.playlist_id}`} className="group overflow-hidden rounded-xl border border-gray-200 bg-white transition hover:border-amber-400 hover:shadow-md">
                                <div className="relative aspect-square bg-gray-200">
                                    {cover
                                        ? <img src={cover} alt="" loading="lazy" className="h-full w-full object-cover" />
                                        : <div className="flex h-full w-full items-center justify-center"><BiSolidPlaylist size={48} className="text-gray-400" /></div>}
                                    {/* Stacked, not opposite corners: two badges collide on a 2-column phone grid. */}
                                    <div className="absolute left-2 top-2 flex flex-col items-start gap-1">
                                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide !text-white ${e.verified ? 'bg-green-600' : 'bg-amber-500'}`}>
                                            {e.verified ? 'Verificada' : 'Por verificar'}
                                        </span>
                                        {e.status === 'private' && (
                                            <span className="rounded-full bg-gray-700 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide !text-white">Privada</span>
                                        )}
                                    </div>
                                </div>
                                <div className="p-2.5 sm:p-3">
                                    <p className="truncate text-sm font-semibold text-gray-900" title={e.title}>{e.title || <span className="font-normal italic text-gray-400">Sem título</span>}</p>
                                    {/* The owner is whose profile the playlist sits on: always an artist,
                                        also when the distributor created it in the panel. */}
                                    <p className="mt-1 flex items-center gap-1.5 text-sm">
                                        <MdPerson size={15} className="shrink-0 text-amber-600" aria-hidden="true" />
                                        <span className="truncate font-medium text-amber-800" title={`Dono: ${e.artist_name || e.name}`}>{e.artist_name || e.name}</span>
                                    </p>
                                    <p className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                                        <span className="flex items-center gap-1" title="Reproduções iniciadas a partir desta playlist">
                                            <MdHeadphones size={13} aria-hidden="true" />{(e.streams_count || 0).toLocaleString('pt-PT')}
                                        </span>
                                        <span className="flex items-center gap-1" title="Pessoas que guardaram esta playlist">
                                            <MdFavorite size={13} aria-hidden="true" />{(e.followers_count || 0).toLocaleString('pt-PT')}
                                        </span>
                                    </p>
                                    {e.created_by_me && <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-indigo-600">Criada por ti</p>}
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
